import React, { Component } from "react";
import { Button } from "primereact/button";
import { Pencil, Trash2, Code, Plus, Check, X, ClipboardPaste, Copy, KeyRound } from "lucide-react";
import DataTableToolbar from "../../components/ui/DataTableToolbar";
import FormField from "../../components/ui/FormField";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Chips } from "primereact/chips";
import { Checkbox } from "primereact/checkbox";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import { AppCard, AppBadge, PageLayout } from "../../components/ui";
import ExtendedDataTable from "../../components/ExtendedDataTable";
import { stringFilterMatchModeOptions, numberFilterMatchModeOptions } from "../Paginator";
import { SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import { utils } from "../../utils/utils";
import { toastService } from "../../services/toastService";
import { confidentialClientApiService } from "../../services/api/clients/ConfidentialClientApiService";
import {
    ConfidentialClientViewModel,
    CreateConfidentialClientRequest,
    UpdateConfidentialClientRequest,
    ConfidentialClientClaimViewModel,
    RefreshTokenExpiration,
} from "../../models/ConfidentialClientModel";

const GRANT_TYPES = ["authorization_code", "client_credentials", "password"];

// Duende IdentityServer's own client defaults, used when creating a client or pasting JSON without them.
const DEFAULT_ABSOLUTE_REFRESH_TOKEN_LIFETIME = 2592000; // 30 days
const DEFAULT_SLIDING_REFRESH_TOKEN_LIFETIME = 1296000; // 15 days

const REFRESH_TOKEN_EXPIRATION_OPTIONS = [
    { label: "Absolute", value: RefreshTokenExpiration.Absolute },
    { label: "Sliding", value: RefreshTokenExpiration.Sliding },
];

type RefreshTokenSettings = Pick<UpdateConfidentialClientRequest, "refreshTokenExpiration" | "absoluteRefreshTokenLifetime" | "slidingRefreshTokenLifetime">;

// Duende ignores the sliding lifetime under Absolute expiration, and the server checks it against the
// absolute lifetime, so the (disabled) field is not sent unless the mode is Sliding.
const withoutUnusedSlidingLifetime = <T extends RefreshTokenSettings>(form: T): T =>
    form.refreshTokenExpiration === RefreshTokenExpiration.Sliding ? form : { ...form, slidingRefreshTokenLifetime: undefined };

const defaultRefreshTokenSettings = (): RefreshTokenSettings => ({
    refreshTokenExpiration: RefreshTokenExpiration.Absolute,
    absoluteRefreshTokenLifetime: DEFAULT_ABSOLUTE_REFRESH_TOKEN_LIFETIME,
    slidingRefreshTokenLifetime: DEFAULT_SLIDING_REFRESH_TOKEN_LIFETIME,
});

interface ConfidentialClientsState {
    clients: ConfidentialClientViewModel[];
    totalRecords: number;
    availableScopes: string[];
    isLoading: boolean;
    showCreateDialog: boolean;
    showEditDialog: boolean;
    showDeleteDialog: boolean;
    showJsonDialog: boolean;
    jsonClient: ConfidentialClientViewModel | null;
    jsonCopied: boolean;
    selectedClient: ConfidentialClientViewModel | null;
    createForm: CreateConfidentialClientRequest;
    editForm: UpdateConfidentialClientRequest;
    newClaimType: string;
    newClaimValue: string;
}

const defaultCreateForm = (): CreateConfidentialClientRequest => ({
    clientName: "",
    allowedGrantTypes: [],
    allowedScopes: [],
    redirectUris: [],
    postLogoutRedirectUris: [],
    allowedCorsOrigins: [],
    accessTokenLifetime: 3600,
    ...defaultRefreshTokenSettings(),
    requirePkce: false,
    requireClientSecret: false,
    allowOfflineAccess: false,
    requireConsent: false,
    allowAccessTokensViaBrowser: false,
    secret: "",
    secretDescription: "",
    claims: [],
});

const defaultEditForm = (): UpdateConfidentialClientRequest => ({
    allowedScopes: [],
    redirectUris: [],
    postLogoutRedirectUris: [],
    allowedCorsOrigins: [],
    accessTokenLifetime: 3600,
    ...defaultRefreshTokenSettings(),
    claims: [],
});

export default class ConfidentialClients extends Component<Record<string, never>, ConfidentialClientsState> {
    private dataTable: ExtendedDataTable | null = null;

    constructor(props: Record<string, never>) {
        super(props);
        this.state = {
            clients: [],
            totalRecords: 0,
            availableScopes: [],
            isLoading: false,
            showCreateDialog: false,
            showEditDialog: false,
            showDeleteDialog: false,
            showJsonDialog: false,
            jsonClient: null,
            jsonCopied: false,
            selectedClient: null,
            createForm: defaultCreateForm(),
            editForm: defaultEditForm(),
            newClaimType: "",
            newClaimValue: "",
        };
    }

    async componentDidMount() {
        await this.loadScopes();
    }

    loadScopes = async () => {
        const result = await confidentialClientApiService.GetScopes();
        if (result.success) {
            this.setState({ availableScopes: result.data });
        }
    };

    loadClientsData = async (searchRequest: SearchRequestBaseModel) => {
        this.setState({ isLoading: true });
        searchRequest.filters = utils.manageDataTableFilters(searchRequest.filters);
        const result = await confidentialClientApiService.Search(searchRequest);
        if (!result.success) {
            toastService.error(result.message);
        } else {
            this.setState({ clients: result.data, totalRecords: result.totalCount });
        }
        this.setState({ isLoading: false });
    };

    // ── Create ──────────────────────────────────────────────────────────────

    openCreateDialog = () => {
        this.setState({ showCreateDialog: true, createForm: defaultCreateForm() });
    };

    onCreateSubmit = async () => {
        const { createForm } = this.state;
        if (!createForm.clientName.trim()) {
            toastService.warn("Client name is required", "Validation");
            return;
        }
        if (createForm.allowedGrantTypes.length === 0) {
            toastService.warn("At least one grant type is required", "Validation");
            return;
        }
        const result = await confidentialClientApiService.Create(withoutUnusedSlidingLifetime(createForm));
        if (!result.success) {
            toastService.error(result.message);
            return;
        }
        toastService.success("Client created");
        this.setState({ showCreateDialog: false });
        this.dataTable?.reset();
    };

    // ── Edit ────────────────────────────────────────────────────────────────

    openEditDialog = (client: ConfidentialClientViewModel) => {
        this.setState({
            selectedClient: client,
            showEditDialog: true,
            editForm: {
                allowedScopes: [...client.allowedScopes],
                redirectUris: [...client.redirectUris],
                postLogoutRedirectUris: [...client.postLogoutRedirectUris],
                allowedCorsOrigins: [...client.allowedCorsOrigins],
                accessTokenLifetime: client.accessTokenLifetime,
                refreshTokenExpiration: client.refreshTokenExpiration,
                absoluteRefreshTokenLifetime: client.absoluteRefreshTokenLifetime,
                slidingRefreshTokenLifetime: client.slidingRefreshTokenLifetime,
                claims: client.claims.map((c) => ({ ...c })),
            },
        });
    };

    onEditSubmit = async () => {
        const { selectedClient, editForm } = this.state;
        if (!selectedClient) return;
        const result = await confidentialClientApiService.Update(selectedClient.clientId, withoutUnusedSlidingLifetime(editForm));
        if (!result.success) {
            toastService.error(result.message);
            return;
        }
        toastService.success("Client updated");
        this.setState({ showEditDialog: false, selectedClient: null });
        this.dataTable?.reset();
    };

    // ── Delete ──────────────────────────────────────────────────────────────

    openDeleteDialog = (client: ConfidentialClientViewModel) => {
        this.setState({ selectedClient: client, showDeleteDialog: true });
    };

    onDeleteConfirm = async () => {
        const { selectedClient } = this.state;
        if (!selectedClient) return;
        const result = await confidentialClientApiService.Delete(selectedClient.clientId);
        if (!result.success) {
            toastService.error(result.message);
            return;
        }
        toastService.success("Client deleted");
        this.setState({ showDeleteDialog: false, selectedClient: null });
        this.dataTable?.reset();
    };

    // ── JSON export ──────────────────────────────────────────────────────────

    clientToJson = (client: ConfidentialClientViewModel): string => {
        const payload = {
            clientName: client.clientId,
            allowedGrantTypes: client.allowedGrantTypes,
            allowedScopes: client.allowedScopes,
            redirectUris: client.redirectUris,
            postLogoutRedirectUris: client.postLogoutRedirectUris,
            allowedCorsOrigins: client.allowedCorsOrigins,
            accessTokenLifetime: client.accessTokenLifetime,
            refreshTokenExpiration: client.refreshTokenExpiration,
            absoluteRefreshTokenLifetime: client.absoluteRefreshTokenLifetime,
            slidingRefreshTokenLifetime: client.slidingRefreshTokenLifetime,
            requirePkce: client.requirePkce,
            requireClientSecret: client.requireClientSecret,
            allowOfflineAccess: client.allowOfflineAccess,
            requireConsent: client.requireConsent,
            allowAccessTokensViaBrowser: client.allowAccessTokensViaBrowser,
            claims: client.claims,
        };
        return JSON.stringify(payload, null, 2);
    };

    openJsonDialog = (client: ConfidentialClientViewModel) => {
        this.setState({ showJsonDialog: true, jsonClient: client, jsonCopied: false });
    };

    copyJson = () => {
        const { jsonClient } = this.state;
        if (!jsonClient) return;
        navigator.clipboard.writeText(this.clientToJson(jsonClient));
        this.setState({ jsonCopied: true });
        setTimeout(() => this.setState({ jsonCopied: false }), 2000);
    };

    pasteJson = async () => {
        try {
            const text = await navigator.clipboard.readText();
            const parsed = JSON.parse(text);
            this.setState((prev) => ({
                createForm: {
                    ...defaultCreateForm(),
                    clientName: parsed.clientName ?? prev.createForm.clientName,
                    allowedGrantTypes: parsed.allowedGrantTypes ?? [],
                    allowedScopes: parsed.allowedScopes ?? [],
                    redirectUris: parsed.redirectUris ?? [],
                    postLogoutRedirectUris: parsed.postLogoutRedirectUris ?? [],
                    allowedCorsOrigins: parsed.allowedCorsOrigins ?? [],
                    accessTokenLifetime: parsed.accessTokenLifetime ?? 3600,
                    refreshTokenExpiration: parsed.refreshTokenExpiration ?? RefreshTokenExpiration.Absolute,
                    absoluteRefreshTokenLifetime: parsed.absoluteRefreshTokenLifetime ?? DEFAULT_ABSOLUTE_REFRESH_TOKEN_LIFETIME,
                    slidingRefreshTokenLifetime: parsed.slidingRefreshTokenLifetime ?? DEFAULT_SLIDING_REFRESH_TOKEN_LIFETIME,
                    requirePkce: parsed.requirePkce ?? false,
                    requireClientSecret: parsed.requireClientSecret ?? false,
                    allowOfflineAccess: parsed.allowOfflineAccess ?? false,
                    requireConsent: parsed.requireConsent ?? false,
                    allowAccessTokensViaBrowser: parsed.allowAccessTokensViaBrowser ?? false,
                    claims: parsed.claims ?? [],
                },
            }));
            toastService.success("Form populated from clipboard JSON", "Pasted");
        } catch {
            toastService.error("Clipboard does not contain valid JSON", "Invalid JSON");
        }
    };

    // ── Helpers ─────────────────────────────────────────────────────────────

    updateCreateForm = (field: keyof CreateConfidentialClientRequest, value: any) => {
        this.setState((prev) => ({ createForm: { ...prev.createForm, [field]: value } }));
    };

    updateEditForm = (field: keyof UpdateConfidentialClientRequest, value: any) => {
        this.setState((prev) => ({ editForm: { ...prev.editForm, [field]: value } }));
    };

    toggleGrantType = (grantType: string) => {
        const current = this.state.createForm.allowedGrantTypes;
        const updated = current.includes(grantType) ? current.filter((g) => g !== grantType) : [...current, grantType];
        this.updateCreateForm("allowedGrantTypes", updated);
    };

    addClaim = (target: "create" | "edit") => {
        const { newClaimType, newClaimValue } = this.state;
        if (!newClaimType.trim() || !newClaimValue.trim()) return;
        const newClaim: ConfidentialClientClaimViewModel = { type: newClaimType.trim(), value: newClaimValue.trim() };
        if (target === "create") {
            this.updateCreateForm("claims", [...this.state.createForm.claims, newClaim]);
        } else {
            this.updateEditForm("claims", [...this.state.editForm.claims, newClaim]);
        }
        this.setState({ newClaimType: "", newClaimValue: "" });
    };

    removeClaim = (target: "create" | "edit", index: number) => {
        if (target === "create") {
            this.updateCreateForm("claims", this.state.createForm.claims.filter((_, i) => i !== index));
        } else {
            this.updateEditForm("claims", this.state.editForm.claims.filter((_, i) => i !== index));
        }
    };

    // ── Column renderers ─────────────────────────────────────────────────────

    grantTypeBody = (rowData: ConfidentialClientViewModel) => (
        <div className="flex gap-1 flex-wrap">
            {rowData.allowedGrantTypes.map((g, i) => (
                <AppBadge key={i} variant={g === "client_credentials" ? "primary" : g === "authorization_code" ? "info" : "neutral"}>
                    {g}
                </AppBadge>
            ))}
        </div>
    );

    secretBody = (rowData: ConfidentialClientViewModel) => (
        <AppBadge variant={rowData.hasSecret ? "success" : "neutral"}>
            {rowData.hasSecret ? "Yes" : "No"}
        </AppBadge>
    );

    actionsBody = (rowData: ConfidentialClientViewModel) => (
        <div className="flex items-center gap-1">
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); this.openEditDialog(rowData); }}
                className="p-1.5 rounded-md text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/20 transition-colors"
                title="Edit"
            >
                <Pencil size={15} />
            </button>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); this.openJsonDialog(rowData); }}
                className="p-1.5 rounded-md text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/20 transition-colors"
                title="Export JSON"
            >
                <Code size={15} />
            </button>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); this.openDeleteDialog(rowData); }}
                className="p-1.5 rounded-md text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:text-danger-400 dark:hover:bg-danger-900/20 transition-colors"
                title="Delete"
            >
                <Trash2 size={15} />
            </button>
        </div>
    );

    renderTableHeader = (hasActiveFilters: boolean) => (
        <DataTableToolbar
            onClear={() => this.dataTable?.reset()}
            hasActiveFilters={hasActiveFilters}
        />
    );

    // ── Claims editor ─────────────────────────────────────────────────────────

    renderClaimsEditor = (claims: ConfidentialClientClaimViewModel[], target: "create" | "edit") => {
        const { newClaimType, newClaimValue } = this.state;
        return (
            <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                    <colgroup>
                        <col style={{ width: "35%" }} />
                        <col style={{ width: "55%" }} />
                        <col style={{ width: "10%" }} />
                    </colgroup>
                    <thead className="bg-neutral-50 dark:bg-neutral-800/50">
                        <tr>
                            <th className="ps-3 py-2 text-left text-xs font-semibold text-neutral-500 dark:text-neutral-400">Type</th>
                            <th className="py-2 text-left text-xs font-semibold text-neutral-500 dark:text-neutral-400">Value</th>
                            <th className="py-2" />
                        </tr>
                    </thead>
                    <tbody>
                        {claims.length === 0 && (
                            <tr>
                                <td colSpan={3} className="text-center text-neutral-400 dark:text-neutral-500 py-3 text-xs">No claims added yet</td>
                            </tr>
                        )}
                        {claims.map((claim, idx) => (
                            <tr key={idx} className="border-t border-neutral-100 dark:border-neutral-800">
                                <td className="ps-3 py-1.5 align-middle"><code className="text-xs text-primary-600 dark:text-primary-400">{claim.type}</code></td>
                                <td className="py-1.5 align-middle text-xs text-neutral-700 dark:text-neutral-300">{claim.value}</td>
                                <td className="py-1.5 align-middle text-center">
                                    <Button icon={<X size={14} />} text severity="danger" size="small" style={{ padding: "2px 6px" }} onClick={() => this.removeClaim(target, idx)} />
                                </td>
                            </tr>
                        ))}
                        <tr className="border-t-2 border-neutral-200 dark:border-neutral-700 bg-neutral-50/50 dark:bg-neutral-800/30">
                            <td className="ps-2 py-1.5">
                                <InputText className="w-full text-xs" placeholder="e.g. role" value={newClaimType} onChange={(e) => this.setState({ newClaimType: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") this.addClaim(target); }} />
                            </td>
                            <td className="py-1.5 pr-1">
                                <InputText className="w-full text-xs" placeholder="e.g. Admin" value={newClaimValue} onChange={(e) => this.setState({ newClaimValue: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") this.addClaim(target); }} />
                            </td>
                            <td className="py-1.5 text-center">
                                <Button icon={<Plus size={14} />} size="small" severity="success" style={{ padding: "3px 8px" }} onClick={() => this.addClaim(target)} />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    // ── Dialogs ──────────────────────────────────────────────────────────────

    renderRefreshTokenFields = (settings: RefreshTokenSettings, onChange: (field: keyof RefreshTokenSettings, value: number) => void) => (
        <FormField.Grid cols={2}>
            <FormField label="Refresh Token Expiration" hint="Sliding renews the token on each use, up to the absolute lifetime" className="sm:col-span-2">
                <Dropdown className="w-full" options={REFRESH_TOKEN_EXPIRATION_OPTIONS} value={settings.refreshTokenExpiration} onChange={(e) => onChange("refreshTokenExpiration", e.value)} />
            </FormField>
            <FormField label="Absolute Refresh Lifetime (seconds)">
                <InputNumber className="w-full" min={1} useGrouping={false} value={settings.absoluteRefreshTokenLifetime} onValueChange={(e) => onChange("absoluteRefreshTokenLifetime", e.value ?? DEFAULT_ABSOLUTE_REFRESH_TOKEN_LIFETIME)} />
            </FormField>
            <FormField label="Sliding Refresh Lifetime (seconds)">
                <InputNumber className="w-full" min={1} useGrouping={false} disabled={settings.refreshTokenExpiration !== RefreshTokenExpiration.Sliding} value={settings.slidingRefreshTokenLifetime} onValueChange={(e) => onChange("slidingRefreshTokenLifetime", e.value ?? DEFAULT_SLIDING_REFRESH_TOKEN_LIFETIME)} />
            </FormField>
        </FormField.Grid>
    );

    renderCreateDialog = () => {
        const { createForm } = this.state;
        return (
            <Dialog
                header={
                    <div className="flex items-center justify-between w-full pr-3">
                        <span>Create Confidential Client</span>
                        <Button label="Paste JSON" icon={<ClipboardPaste size={16} />} outlined severity="secondary" size="small" onClick={this.pasteJson} tooltip="Paste a copied client JSON to auto-fill the form" tooltipOptions={{ position: "left" }} />
                    </div>
                }
                visible={this.state.showCreateDialog}
                style={{ width: "600px" }}
                onHide={() => this.setState({ showCreateDialog: false })}
                contentStyle={{ maxHeight: "70vh", overflowY: "auto" }}
                footer={
                    <>
                        <Button label="Cancel" outlined onClick={() => this.setState({ showCreateDialog: false })} />
                        <Button label="Create" icon={<Check size={16} />} onClick={this.onCreateSubmit} />
                    </>
                }
            >
                <div className="space-y-5">
                    <FormField label="Client Name" required>
                        <InputText className="w-full" value={createForm.clientName} onChange={(e) => this.updateCreateForm("clientName", e.target.value)} />
                    </FormField>
                    <FormField label="Grant Types" required>
                        <div className="flex gap-4 mt-1">
                            {GRANT_TYPES.map((g) => (
                                <div key={g} className="flex items-center gap-2">
                                    <Checkbox inputId={g} checked={createForm.allowedGrantTypes.includes(g)} onChange={() => this.toggleGrantType(g)} />
                                    <label htmlFor={g} className="mb-0 text-sm text-neutral-700 dark:text-neutral-300">{g}</label>
                                </div>
                            ))}
                        </div>
                    </FormField>
                    <FormField label="Allowed Scopes">
                        <MultiSelect className="w-full" options={this.state.availableScopes} value={createForm.allowedScopes} onChange={(e) => this.updateCreateForm("allowedScopes", e.value ?? [])} filter placeholder="Select scopes" display="chip" />
                    </FormField>
                    <FormField label="Redirect URIs">
                        <Chips className="w-full" value={createForm.redirectUris} onChange={(e) => this.updateCreateForm("redirectUris", e.value ?? [])} />
                    </FormField>
                    <FormField label="Post Logout Redirect URIs">
                        <Chips className="w-full" value={createForm.postLogoutRedirectUris} onChange={(e) => this.updateCreateForm("postLogoutRedirectUris", e.value ?? [])} />
                    </FormField>
                    <FormField label="Allowed CORS Origins">
                        <Chips className="w-full" value={createForm.allowedCorsOrigins} onChange={(e) => this.updateCreateForm("allowedCorsOrigins", e.value ?? [])} />
                    </FormField>
                    <FormField.Grid cols={2}>
                        <FormField label="Access Token Lifetime (seconds)">
                            <InputNumber className="w-full" value={createForm.accessTokenLifetime} onValueChange={(e) => this.updateCreateForm("accessTokenLifetime", e.value ?? 3600)} />
                        </FormField>
                        <FormField label="Secret">
                            <InputText className="w-full" value={createForm.secret ?? ""} onChange={(e) => this.updateCreateForm("secret", e.target.value)} />
                        </FormField>
                    </FormField.Grid>
                    {this.renderRefreshTokenFields(createForm, this.updateCreateForm)}
                    <FormField label="Claims">
                        {this.renderClaimsEditor(createForm.claims, "create")}
                    </FormField>
                    <div className="flex flex-wrap gap-4 pt-1">
                        {[
                            { field: "requirePkce", label: "Require PKCE" },
                            { field: "requireClientSecret", label: "Require Client Secret" },
                            { field: "allowOfflineAccess", label: "Allow Offline Access" },
                            { field: "allowAccessTokensViaBrowser", label: "Allow Access Tokens Via Browser" },
                        ].map(({ field, label }) => (
                            <div key={field} className="flex items-center gap-2">
                                <Checkbox inputId={field} checked={(createForm as any)[field]} onChange={(e) => this.updateCreateForm(field as keyof CreateConfidentialClientRequest, e.checked)} />
                                <label htmlFor={field} className="mb-0 text-sm text-neutral-700 dark:text-neutral-300">{label}</label>
                            </div>
                        ))}
                    </div>
                </div>
            </Dialog>
        );
    };

    renderEditDialog = () => {
        const { editForm, selectedClient } = this.state;
        return (
            <Dialog
                header={`Edit: ${selectedClient?.clientId ?? ""}`}
                visible={this.state.showEditDialog}
                style={{ width: "600px" }}
                onHide={() => this.setState({ showEditDialog: false })}
                contentStyle={{ maxHeight: "70vh", overflowY: "auto" }}
                footer={
                    <>
                        <Button label="Cancel" outlined onClick={() => this.setState({ showEditDialog: false })} />
                        <Button label="Save" icon={<Check size={16} />} onClick={this.onEditSubmit} />
                    </>
                }
            >
                <div className="space-y-5">
                    <FormField label="Allowed Scopes">
                        <MultiSelect className="w-full" options={this.state.availableScopes} value={editForm.allowedScopes} onChange={(e) => this.updateEditForm("allowedScopes", e.value ?? [])} filter placeholder="Select scopes" display="chip" />
                    </FormField>
                    <FormField label="Redirect URIs">
                        <Chips className="w-full" value={editForm.redirectUris} onChange={(e) => this.updateEditForm("redirectUris", e.value ?? [])} />
                    </FormField>
                    <FormField label="Post Logout Redirect URIs">
                        <Chips className="w-full" value={editForm.postLogoutRedirectUris} onChange={(e) => this.updateEditForm("postLogoutRedirectUris", e.value ?? [])} />
                    </FormField>
                    <FormField label="Allowed CORS Origins">
                        <Chips className="w-full" value={editForm.allowedCorsOrigins} onChange={(e) => this.updateEditForm("allowedCorsOrigins", e.value ?? [])} />
                    </FormField>
                    <FormField label="Access Token Lifetime (seconds)">
                        <InputNumber className="w-full" value={editForm.accessTokenLifetime} onValueChange={(e) => this.updateEditForm("accessTokenLifetime", e.value ?? 3600)} />
                    </FormField>
                    {this.renderRefreshTokenFields(editForm, this.updateEditForm)}
                    <FormField label="Claims">
                        {this.renderClaimsEditor(editForm.claims, "edit")}
                    </FormField>
                </div>
            </Dialog>
        );
    };

    renderDeleteDialog = () => (
        <Dialog
            header="Confirm Delete"
            visible={this.state.showDeleteDialog}
            style={{ width: "400px" }}
            onHide={() => this.setState({ showDeleteDialog: false })}
            footer={
                <>
                    <Button label="Cancel" outlined onClick={() => this.setState({ showDeleteDialog: false })} />
                    <Button label="Delete" icon={<Trash2 size={16} />} severity="danger" onClick={this.onDeleteConfirm} />
                </>
            }
        >
            <p>Are you sure you want to delete <strong>{this.state.selectedClient?.clientId}</strong>?</p>
        </Dialog>
    );

    renderJsonDialog = () => {
        const { jsonClient, jsonCopied } = this.state;
        if (!jsonClient) return null;
        const json = this.clientToJson(jsonClient);
        return (
            <Dialog
                header={
                    <div className="flex items-center gap-2">
                        <Code size={18} />
                        <span>{jsonClient.clientId}</span>
                        <AppBadge variant="neutral">JSON</AppBadge>
                    </div>
                }
                visible={this.state.showJsonDialog}
                style={{ width: "680px" }}
                onHide={() => this.setState({ showJsonDialog: false, jsonClient: null })}
                footer={
                    <div className="flex justify-between items-center w-full">
                        <small className="text-neutral-500 dark:text-neutral-400">Ready to use as a <code>POST /client/confidential</code> body in another environment</small>
                        <Button
                            label={jsonCopied ? "Copied!" : "Copy JSON"}
                            icon={jsonCopied ? <Check size={16} /> : <Copy size={16} />}
                            severity={jsonCopied ? "success" : undefined}
                            outlined={!jsonCopied}
                            onClick={this.copyJson}
                        />
                    </div>
                }
            >
                <textarea
                    readOnly
                    value={json}
                    className="w-full font-mono text-xs leading-relaxed p-3 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-neutral-50 dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200 resize-y outline-none"
                    style={{ height: "420px" }}
                    onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                />
                <small className="text-neutral-400 dark:text-neutral-500 block mt-1">Click inside to select all · Secret is excluded for security</small>
            </Dialog>
        );
    };

    render() {
        return (
            <PageLayout heading="Confidential Clients" icon={KeyRound} actions={<Button label="Add Client" icon={<Plus size={16} />} onClick={this.openCreateDialog} />}>
                <AppCard>
                    <AppCard.Body className="!p-0">
                        <ExtendedDataTable
                            ref={(ref) => (this.dataTable = ref)}
                            header={this.renderTableHeader}
                            value={this.state.clients}
                            onChange={this.loadClientsData}
                            loading={this.state.isLoading}
                            totalRecords={this.state.totalRecords}
                            defaultSortField="clientId"
                            defaultSortOrder={SortOrderType.Ascending}
                        >
                            <Column {...({ alwaysVisible: true } as any)} field="clientId" header="Client ID" filterMatchModeOptions={stringFilterMatchModeOptions} />
                            <Column {...({ alwaysVisible: true } as any)} field="clientName" header="Client Name" filterMatchModeOptions={stringFilterMatchModeOptions} />
                            <Column {...({ alwaysVisible: true } as any)} header="Grant Types" body={this.grantTypeBody} />
                            <Column field="accessTokenLifetime" header="Token Lifetime (s)" filterMatchModeOptions={numberFilterMatchModeOptions} />
                            <Column header="Has Secret" body={this.secretBody} />
                            <Column header="Actions" body={this.actionsBody} style={{ width: "160px" }} />
                        </ExtendedDataTable>
                    </AppCard.Body>
                </AppCard>
                {this.renderCreateDialog()}
                {this.renderEditDialog()}
                {this.renderDeleteDialog()}
                {this.renderJsonDialog()}
            </PageLayout>
        );
    }
}
